//defs
import { ShowPaymentsProps } from "@payment-client/components/show-payments";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

//internal-deps
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
import {
    isAmazonPayLater,
    PayLaterPaymentGroupActions,
    SwiggyInstrumentsPaymentGroupActions,
    TurboPaymentMethod,
    TurboPaymentSpecificActions,
    WalletPaymentGroupActions,
} from "@swiggy-private/payment-ui";
import { isAmazonWallet } from "@swiggy-private/payment-ui";
import { SESSION_STORAGE_KEYS, Storage } from "@swiggy-private/core-ui";
import { PlaceOrderData, NEWRELIC_TABLES, recordNREvent, isBusinessLineDash } from "@swiggy-private/checkout-core";
import { recordAplTurboWidgetDecision } from "@payment-client/helpers/AmazonPayNREvents";
import { ApiFetchState, WalletApiFetchState } from "@swiggy-private/payment-core";
import { isEmpty } from "@swiggy-private/js-utils";

//helpers
import { getPaymentAmount, isCardPaymentMethod, isFoodCard, isFoodCardBalanceSufficient } from "./PaymentHelper";

//helpers
import { isUpiIntentPaymentMethod, shouldShowThirdPartyUpiIntentApps } from "./UpiHelper";
import { isWalletPaymentMethod } from "./WalletHelper";
import { isSwiggyInstrumentPaymentMethod } from "./SwiggyInstrumentsHelper";
import { isPayLaterMethod } from "./PaylaterHelper";
import { isCredPaymentMethod } from "./CredHelper";
import { isBalanceSufficient } from "./PaymentFallbackHelper";
import { isInAppUpiPaymentMethod } from "./InAppUpiHelper";

// constants
import { SDK_TURBO_PLACE_ORDER_TIME } from "@payment-client/constants/StorageKeys";
import { SDK_TURBO_PLACE_ORDER_TIMEOUT_IN_MINS } from "@payment-client/constants/Turbo";

export const isTurboPaymentGroup = (paymentGroup: GetPaymentMethodsResponse["paymentGroup"][0]) =>
    paymentGroup?.groupName?.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.SINGLE_CLICK;

const removeNotAvailableUpiIntentMethods = ({
    turboPaymentMethods,
    availableUpiIntentMethods,
}: {
    turboPaymentMethods: TurboPaymentMethod[];
    availableUpiIntentMethods: string[];
}) =>
    turboPaymentMethods.filter((turboPaymentMethod) => {
        if (!isUpiIntentPaymentMethod(turboPaymentMethod)) {
            return true;
        }
        return availableUpiIntentMethods.includes(turboPaymentMethod.name);
    });

export const shouldHideTurboPaymentWidget = ({
    turboPaymentMethod,
    billTotal,
    isSdkAvailableForCred,
    isSdkAvailableForInAppUpi,
    getTurboPaymentSpecificActions,
}: {
    turboPaymentMethod: TurboPaymentMethod;
    billTotal: number;
    isSdkAvailableForCred: boolean;
    isSdkAvailableForInAppUpi: boolean;
    getTurboPaymentSpecificActions: (groupName: string) => TurboPaymentSpecificActions;
}) => {
    if (!turboPaymentMethod) {
        return { hide: true };
    }
    const groupName = turboPaymentMethod?.groupName?.toUpperCase();
    if (isCardPaymentMethod(turboPaymentMethod)) {
        if (isFoodCard(turboPaymentMethod)) {
            const foodCardBalance = Number(turboPaymentMethod.meta.cardBalance);
            if (!isFoodCardBalanceSufficient(foodCardBalance, billTotal)) {
                return { hide: true };
            }
            return { hide: false, balanceValue: foodCardBalance };
        }
    } else if (isWalletPaymentMethod(turboPaymentMethod)) {
        const walletPaymentActions = getTurboPaymentSpecificActions(groupName) as WalletPaymentGroupActions;

        const isAPayWallet = isAmazonWallet(turboPaymentMethod);

        if (isAPayWallet) {
            const { state: amazonPaySdkStatus, sdkFetchInProgress } = walletPaymentActions.getAmazonPaySdkStatus();
            const aPaySdkAvailable = (amazonPaySdkStatus && amazonPaySdkStatus.available) || false;

            // loading state
            if (sdkFetchInProgress) {
                return { loading: true };
            }

            // error state
            if (!aPaySdkAvailable || !amazonPaySdkStatus || !amazonPaySdkStatus.linked || !amazonPaySdkStatus.balance) {
                return { hide: true };
            }

            const _isBalanceSufficient = isBalanceSufficient(billTotal, Number(amazonPaySdkStatus.balance));
            if (!_isBalanceSufficient) {
                return { hide: true };
            }

            // success state
            return { hide: false, balanceValue: amazonPaySdkStatus.balance };
        } else {
            const [fetchState, walletData] = walletPaymentActions.getWalletBalance(turboPaymentMethod);
            const walletBalanceLoading =
                fetchState === WalletApiFetchState.LOADING || fetchState === WalletApiFetchState.DEFAULT;

            // loading state
            if (walletBalanceLoading) {
                return { loading: true };
            }

            // error state
            if (!walletData || !walletData.linked || !walletData.isWalletBalanceSufficent) {
                return { hide: true };
            }

            // success state
            return { hide: false, balanceValue: walletData.balance };
        }
    } else if (isSwiggyInstrumentPaymentMethod(turboPaymentMethod)) {
        const { getSwiggyInstrumentsData } = getTurboPaymentSpecificActions(
            groupName,
        ) as SwiggyInstrumentsPaymentGroupActions;
        const swiggyInstrumentsData = getSwiggyInstrumentsData(turboPaymentMethod);
        if (!swiggyInstrumentsData.isBalanceSufficient) {
            return { hide: true };
        }

        return { hide: false, balanceValue: swiggyInstrumentsData.balance };
    } else if (isPayLaterMethod(turboPaymentMethod)) {
        const paylaterActions = getTurboPaymentSpecificActions(groupName) as PayLaterPaymentGroupActions;
        const [fetchState, payLaterLinkedData] = paylaterActions.checkPayLaterLinkStatus(turboPaymentMethod);
        const linkStatusLoading = fetchState === ApiFetchState.LOADING;
        const isApl = isAmazonPayLater(turboPaymentMethod.paymentCode);

        if (linkStatusLoading) {
            // If wallet balance is loading, we need to handle the turbopayment component
            // active till the valid value is received
            return { loading: true };
        } else if (!payLaterLinkedData.linked || payLaterLinkedData.isCreditLimitSufficient === false) {
            if (isApl) {
                recordAplTurboWidgetDecision({
                    hide: true,
                    reason: !payLaterLinkedData.linked ? "not_linked" : "insufficient_credit",
                });
            }
            return { hide: true };
        } else {
            const shouldHide = !turboPaymentMethod.enabled;
            if (isApl) {
                recordAplTurboWidgetDecision({
                    hide: shouldHide,
                    reason: shouldHide ? "disabled" : "visible",
                    extra: { creditLimit: payLaterLinkedData.creditLimit ?? "--" },
                });
            }
            return {
                hide: shouldHide,
                balanceValue: payLaterLinkedData.creditLimit,
                keyFactStatementUrl: payLaterLinkedData.keyFactStatementUrl,
            };
        }
    } else if (isCredPaymentMethod(turboPaymentMethod)) {
        if (!isSdkAvailableForCred) {
            return { hide: true };
        }
    } else if (isInAppUpiPaymentMethod(turboPaymentMethod)) {
        if (!isSdkAvailableForInAppUpi) {
            return { hide: true };
        }

        const liteBalance = turboPaymentMethod?.["meta"]?.["liteBalance"];
        // lite balance only exists in case of upi lite
        if (liteBalance) {
            const _liteBalance = Number(liteBalance ?? 0);
            const _isBalanceSufficient = isBalanceSufficient(billTotal, _liteBalance);
            if (!_isBalanceSufficient) {
                return { hide: true };
            }

            return { hide: false, balanceValue: liteBalance };
        }
    }
    return { hide: !turboPaymentMethod.enabled };
};

export const getTurboPaymentMethod = ({
    paymentResponse,
    availableUpiIntentMethods,
    billTotal,
    isSdkAvailableForCred,
    isSdkAvailableForInAppUpi,
    getTurboPaymentSpecificActions,
}: {
    paymentResponse: GetPaymentMethodsResponse | null | undefined;
    availableUpiIntentMethods: string[];
    billTotal: number;
    isSdkAvailableForCred: boolean;
    isSdkAvailableForInAppUpi: boolean;
    getTurboPaymentSpecificActions: (groupName: string) => TurboPaymentSpecificActions;
}) => {
    if (!paymentResponse) {
        return { hide: false, turboPaymentMethod: null };
    }

    const turboPaymentGroup = paymentResponse.paymentGroup.find((paymentGroup) => isTurboPaymentGroup(paymentGroup));
    const turboPaymentMethods = turboPaymentGroup?.paymentMethods || [];
    const turboPaymentsWithAvailableUpiMethods = removeNotAvailableUpiIntentMethods({
        availableUpiIntentMethods,
        turboPaymentMethods,
    });
    const {
        hide,
        balanceValue,
        loading = false,
        keyFactStatementUrl,
    } = shouldHideTurboPaymentWidget({
        turboPaymentMethod: turboPaymentsWithAvailableUpiMethods[0],
        billTotal,
        isSdkAvailableForCred,
        isSdkAvailableForInAppUpi,
        getTurboPaymentSpecificActions,
    });
    return {
        hide,
        turboPaymentMethod: !hide ? turboPaymentsWithAvailableUpiMethods[0] : null,
        balanceValue,
        loading,
        keyFactStatementUrl,
    };
};

export const getTurboPaymentEligibilityMeta = (
    props: ShowPaymentsProps,
): {
    isTurboPaymentMethod: boolean;
    turboPayUniqueId: string;
    isTurboV2: boolean;
    isBillToCompanySelected?: boolean;
} => {
    if ("isTurboPaymentMethod" in props && "turboPayUniqueId" in props) {
        return {
            isTurboPaymentMethod: props.isTurboPaymentMethod,
            turboPayUniqueId: props.turboPayUniqueId,
            isTurboV2: props.isTurboV2 === true,
            isBillToCompanySelected: props.isBillToCompanySelected,
        };
    }
    return {
        isTurboPaymentMethod: false,
        isTurboV2: false,
        turboPayUniqueId: "",
        isBillToCompanySelected: false,
    };
};

export const getTurboPaymentMethodFromPaymentResponse = (
    paymentResponse: GetPaymentMethodsResponse | null | undefined,
) => {
    return paymentResponse?.paymentGroup?.find((group) => isTurboPaymentGroup(group));
};

export const transformPaymentResponseForTurboPay = (
    paymentResponse: GetPaymentMethodsResponse,
    isTurboPaymentMethod: boolean,
): GetPaymentMethodsResponse => {
    if (!paymentResponse || !paymentResponse.paymentGroup) {
        return paymentResponse;
    }

    if (isTurboPaymentMethod) {
        const turboPaymentGroups = paymentResponse.paymentGroup.filter((group) => isTurboPaymentGroup(group));
        return {
            ...paymentResponse,
            // in case of turbo flow, we need to pass splitPayMethods as default so that
            // the amount computations are always done without splitpay, this is because amountToPay can vary with/without splitpay
            // and filtering criteria of sufficient balance will break in both the cases
            splitpayList: undefined,
            paymentGroup: turboPaymentGroups,
            shouldShowThirdPartyUpiIntentApps: shouldShowThirdPartyUpiIntentApps(paymentResponse),
        };
    }

    const nonTurboPaymentGroup = paymentResponse.paymentGroup.filter(
        (paymentGroup) => !isTurboPaymentGroup(paymentGroup),
    );
    return {
        ...paymentResponse,
        paymentGroup: nonTurboPaymentGroup,
        shouldShowThirdPartyUpiIntentApps: shouldShowThirdPartyUpiIntentApps(paymentResponse),
    };
};

export const getTurboPaymentMethodFromPlaceOrder = (placeOrderData?: PlaceOrderData) => {
    return String(placeOrderData?.extraArbitaryData?.isTurboPaymentMethod ?? "--");
};

export const isSdkTurboPlaceOrderTimeout = (): boolean => {
    const sdkPlaceOrderTimestamp = Number(Storage.getItem(SDK_TURBO_PLACE_ORDER_TIME));

    if (isEmpty(sdkPlaceOrderTimestamp)) {
        return true;
    }
    const currentTime = new Date().getTime();

    // calculating the time since turbo place order happened in minutes.
    const timeSinceTurboPlaceOrderInMins = (currentTime - sdkPlaceOrderTimestamp) / (60 * 1000);

    // If the last turbo place order time is > 15 mins, then we are treating the order is timed out
    return timeSinceTurboPlaceOrderInMins > SDK_TURBO_PLACE_ORDER_TIMEOUT_IN_MINS;
};

// This function would only work for non POR flows (IM)
export const checkAndRecordAmountMismatch = (
    paymentResponse: GetPaymentMethodsResponse,
    isTurboPaymentMethod: boolean,
) => {
    const billAmountShownOnCart = Storage.getItem<string>(SESSION_STORAGE_KEYS.BILL_AMOUNT_SHOWN_ON_CART);

    if (isEmpty(paymentResponse) || isEmpty(billAmountShownOnCart) || !isBusinessLineDash() || !isTurboPaymentMethod) {
        return;
    }

    const withCoupon = Storage.getItem<boolean>(SESSION_STORAGE_KEYS.WITH_COUPON);

    const _withCoupon = withCoupon && paymentResponse.couponApplied === true;
    const billTotal = Number(getPaymentAmount(paymentResponse, _withCoupon).unwrapOr("")) || 0;

    if (billAmountShownOnCart !== String(billTotal)) {
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_CART_AMOUNT_MISMATCH_V3,
            billAmountShownOnCart,
            billTotal: String(billTotal),
        });
    }
};

export const saveTurboPayFallbackConfig = (turboPayUniqueId: string) => {
    Storage.setItem(SESSION_STORAGE_KEYS.TURBO_PAY_FALLBACK_CONFIG, turboPayUniqueId);
};

export const getTurboPayFallbackConfig = () => {
    return Storage.getItem<string>(SESSION_STORAGE_KEYS.TURBO_PAY_FALLBACK_CONFIG);
};

export const clearTurboPayFallbackConfig = () => {
    Storage.removeItem(SESSION_STORAGE_KEYS.TURBO_PAY_FALLBACK_CONFIG);
};
